import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button } from 'react-bootstrap';
import { Seller, SellerLocation } from '../../../../api/shop/types/sellerTypes';
import { StatusBadge, FulfillmentBadge, TypeBadge } from '../../../../Common/BadgeStyles';
import '../../../../Common/BadgeStyles.css'; // Ensure badge styles are loaded
import { getImageUrl } from '../../../../utils/imageUrl';
import './SellerCard.css';

interface SellerCardProps {
  seller?: Seller;
  location?: SellerLocation;
  onEdit?: () => void;
  onDelete?: () => void;
  onView?: () => void;
  onToggleStatus?: () => void;
}

const SellerCard: React.FC<SellerCardProps> = ({
  seller,
  location,
  onEdit,
  onDelete,
  onView,
  onToggleStatus,
}) => {
  const navigate = useNavigate();
  const isLocation = !!location;
  const data = location || seller;
  
  if (!data) return null;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const getSellerTypeLabel = (type: string) => {
    return type === 'PLATFORM_OWNED' ? 'Platform Owned' : 'Third Party';
  };

  const formatAddress = () => {
    if (!location?.address) return 'N/A';
    const { address } = location;
    return `"${address.line1}", ${address.city}, ${address.state}, ${address.pincode}`;
  };

  const fulfillmentCapabilities = isLocation 
    ? location.fulfillmentCapabilities 
    : seller?.supportedFulfillment || [];

  const handleCardClick = () => {
    if (isLocation && location) {
      // Navigate to location detail page
      navigate(`/shop-services/sellers/locations/${location._id}`);
    } else if (onView) {
      onView();
    }
  };

  return (
    <Card className="seller-card" onClick={handleCardClick}>
      <Card.Body className="seller-card-body">
        {/* Status Badge - Positioned absolutely at top-right */}
        <div className="seller-card-badge-container">
          <StatusBadge status={data.status as 'ACTIVE' | 'INACTIVE'} />
        </div>
        
        {/* Header: Name and ID (only for sellers) */}
        <div className="seller-card-header">
          <h5 className="mb-1 seller-card-title">
            {isLocation ? (location.name || 'Unnamed Location') : seller?.name}
          </h5>
          {!isLocation && seller && (
            <p className="mb-0 seller-card-id">
              ID: {seller.id}
            </p>
          )}
        </div>

        {/* Two Column Layout: Created at and Type */}
        <div className="row mb-2 seller-card-info-row">
          <div className="col-6 seller-card-info-col-left">
            <p className="mb-1 seller-card-label">
              Created at
            </p>
            <p className="mb-0 seller-card-value">
              {formatDate(data.createdAt || (data as any).createdDate)}
            </p>
          </div>
          <div className="col-6 seller-card-info-col-right">
            <p className="mb-1 seller-card-label">
              {isLocation ? 'Location Code' : 'Seller Type'}
            </p>
            <p className="mb-0 seller-card-value">
              {isLocation 
                ? location.locationCode 
                : seller ? getSellerTypeLabel(seller.type) : 'N/A'}
            </p>
          </div>
        </div>

        {/* Fulfillment Capabilities and Type (for locations) or Fulfillment Capabilities only (for sellers) */}
        {isLocation ? (
          <div className="row mb-2 seller-card-info-row">
            <div className="col-6 seller-card-info-col-left">
              <p className="mb-1 seller-card-label">
                Fulfillment Capabilities
              </p>
              <div className="seller-card-fulfillment-badges">
                {(() => {
                  const sortedCapabilities = [...fulfillmentCapabilities].sort((a, b) => {
                    if (a === 'PICKUP') return -1;
                    if (b === 'PICKUP') return 1;
                    if (a === 'DELIVERY') return -1;
                    if (b === 'DELIVERY') return 1;
                    return 0;
                  });
                  return sortedCapabilities.map((capability) => (
                    <FulfillmentBadge 
                      key={capability} 
                      capability={capability as 'PICKUP' | 'DELIVERY'} 
                    />
                  ));
                })()}
              </div>
            </div>
            <div className="col-6 seller-card-info-col-right">
              <p className="mb-1 seller-card-label">
                Type
              </p>
              <TypeBadge type={location.type} />
            </div>
          </div>
        ) : (
          <div className="mb-2 seller-card-fulfillment">
            <p className="mb-1 seller-card-label seller-card-fulfillment-label">
              Fulfillment Capabilities
            </p>
            <div className="seller-card-fulfillment-badges">
              {(() => {
                const sortedCapabilities = [...fulfillmentCapabilities].sort((a, b) => {
                  if (a === 'PICKUP') return -1;
                  if (b === 'PICKUP') return 1;
                  if (a === 'DELIVERY') return -1;
                  if (b === 'DELIVERY') return 1;
                  return 0;
                });
                return sortedCapabilities.map((capability) => (
                  <FulfillmentBadge 
                    key={capability} 
                    capability={capability as 'PICKUP' | 'DELIVERY'} 
                  />
                ));
              })()}
            </div>
          </div>
        )}

        {/* Address (only for locations) */}
        {isLocation && (
          <div className="mb-2 seller-card-fulfillment">
            <p className="mb-1 seller-card-label seller-card-fulfillment-label">
              Address
            </p>
            <p className="mb-0 seller-card-value" style={{ wordBreak: 'break-word', lineHeight: '18px' }}>
              {formatAddress()}
            </p>
          </div>
        )}

        {/* Bottom Actions: Toggle and Buttons (only when callbacks provided) */}
        {(onToggleStatus || onEdit || onDelete) && (
          <div
            className={`d-flex ${
              isLocation ? 'justify-content-center' : 'justify-content-between'
            } align-items-center mt-auto seller-card-actions ${
              isLocation ? 'location-actions' : ''
            }`}
          >
            {!isLocation && onToggleStatus && (
              <div className="form-check form-switch">
                <input
                  className={`form-check-input seller-card-toggle ${
                    data.status === 'ACTIVE' ? 'active' : 'inactive'
                  }`}
                  type="checkbox"
                  role="switch"
                  checked={data.status === 'ACTIVE'}
                  onChange={(e) => {
                    e.stopPropagation();
                    onToggleStatus();
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
            <div className="d-flex gap-2 seller-card-buttons-container">
              {isLocation ? (
                <>
                  {onEdit && (
                    <Button
                      variant="primary"
                      size="sm"
                      className="seller-action-btn seller-edit-btn seller-edit-btn-solid"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit();
                      }}
                    >
                      Edit Seller
                      <img
                        src={getImageUrl('admin/static-assets/icons/general/edit-icon.svg')}
                        alt="Edit"
                        className="seller-icon"
                        style={{ width: '14px', height: '14px' }}
                      />
                    </Button>
                  )}
                  {onToggleStatus && (
                    <Button
                      variant="outline-warning"
                      size="sm"
                      className="seller-action-btn seller-deactivate-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleStatus();
                      }}
                    >
                      {data.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="seller-action-btn seller-delete-btn seller-delete-btn-icon-only"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                    >
                      <img
                        src={getImageUrl('admin/static-assets/icons/general/delete-icon.svg')}
                        alt="Delete"
                        className="seller-icon"
                        style={{ width: '16px', height: '16px' }}
                      />
                    </Button>
                  )}
                </>
              ) : (
                <>
                  {seller && onEdit && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="seller-action-btn seller-edit-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/shop-services/sellers/${seller._id}`);
                      }}
                    >
                      Edit Seller
                      <img
                        src={getImageUrl('admin/static-assets/icons/general/edit-icon.svg')}
                        alt="Edit"
                        className="seller-icon"
                        style={{ width: '14px', height: '14px' }}
                      />
                    </Button>
                  )}
                  {onDelete && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="seller-action-btn seller-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                      }}
                    >
                      Delete Seller
                      <img
                        src={getImageUrl('admin/static-assets/icons/general/delete-icon.svg')}
                        alt="Delete"
                        className="seller-icon"
                        style={{ width: '14px', height: '14px' }}
                      />
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SellerCard;
